export const ADMIN_WEB_DEVICE = 3;
export const PASSWORD_LOGIN_TYPE = 1;
export const SILENT_LOGIN_TYPE = 2;
export const SILENT_LOGIN_REQUEST_TIMEOUT_MS = 60000;
export const REFRESH_LOCK_TTL_MS = SILENT_LOGIN_REQUEST_TIMEOUT_MS + 5000;
export const REFRESH_WAIT_TIMEOUT_MS = REFRESH_LOCK_TTL_MS + 2000;
export const ACCESS_TOKEN_INITIAL_TTL_MS = 2 * 60 * 60 * 1000;
export const ACCESS_TOKEN_ACTIVITY_TTL_MS = 60 * 60 * 1000;
export const ACCESS_TOKEN_REFRESH_SKEW_MS = 30 * 1000;
export const NETWORK_RETRY_DELAY_MS = 300;

const SILENT_LOGIN_RETRY_FLAG = '__retryAfterSilentLogin';
const NETWORK_RETRY_FLAG = '__retryAfterNetworkError';

export function joinApiUrl(baseUrl, path) {
    const base = String(baseUrl || '').replace(/\/+$/, '');
    const suffix = String(path || '').replace(/^\/+/, '');
    return suffix ? `${base}/${suffix}` : (base || '/');
}

export function isAdminAuthRequest(url = '') {
    const normalizedUrl = String(url).toLowerCase();
    return normalizedUrl.includes('/adminapi/login')
        || normalizedUrl.includes('/adminapi/outlogin');
}

function normalizeExpiry(value) {
    const expiry = Number(value);
    return Number.isFinite(expiry) && expiry > 0 ? expiry : null;
}

export function getAccessTokenState(token, expiresAt, now = Date.now()) {
    if (!token) {
        return 'missing';
    }
    const expiry = normalizeExpiry(expiresAt);
    if (expiry == null) {
        return 'unknown';
    }
    if (expiry <= now) {
        return 'expired';
    }
    if (expiry - now <= ACCESS_TOKEN_REFRESH_SKEW_MS) {
        return 'expiring';
    }
    return 'fresh';
}

export function shouldRefreshAccessToken(token, expiresAt, now = Date.now()) {
    const state = getAccessTokenState(token, expiresAt, now);
    return state === 'unknown' || state === 'expiring' || state === 'expired';
}

export function isAccessTokenKnownExpired(token, expiresAt, now = Date.now()) {
    return getAccessTokenState(token, expiresAt, now) === 'expired';
}

export function canRetryAfterSilentLogin(config) {
    return Boolean(
        config
        && !config[SILENT_LOGIN_RETRY_FLAG]
        && !isAdminAuthRequest(config.url)
    );
}

export function prepareSilentLoginRetry(config, token, lang) {
    if (!canRetryAfterSilentLogin(config)) {
        return false;
    }

    config[SILENT_LOGIN_RETRY_FLAG] = true;
    config.headers = config.headers || {};
    config.headers.token = token;
    config.headers.lang = lang;
    return true;
}

export function isNetworkError(error) {
    if (!error || error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
        return false;
    }
    return error.code === 'ERR_NETWORK'
        || error.message === 'Network Error'
        || Boolean(error.request);
}

export function canRetryNetworkRequest(config) {
    return Boolean(
        config
        && !config[NETWORK_RETRY_FLAG]
        && !isAdminAuthRequest(config.url)
    );
}

export function canRetryAuthenticatedNetworkError(error, config, token) {
    return Boolean(token) && isNetworkError(error) && canRetryNetworkRequest(config);
}

export function prepareNetworkRetry(config) {
    if (!canRetryNetworkRequest(config)) {
        return false;
    }
    config[NETWORK_RETRY_FLAG] = true;
    return true;
}

export function waitForNetworkRetry(delay = NETWORK_RETRY_DELAY_MS) {
    return new Promise((resolve) => setTimeout(resolve, delay));
}

export async function runWithNetworkRetry(run, wait = waitForNetworkRetry) {
    try {
        return await run();
    } catch (error) {
        if (!isNetworkError(error)) {
            throw error;
        }
        await wait(NETWORK_RETRY_DELAY_MS);
        return run();
    }
}

export function isHttpUnauthorized(status) {
    return Number(status) === 401;
}

export function shouldRetryHttpUnauthorized(status, config) {
    return isHttpUnauthorized(status) && !isAdminAuthRequest(config?.url);
}

export function isSameAuthenticatedUser(currentUserId, incomingUserId) {
    if (currentUserId == null || currentUserId === ''
        || incomingUserId == null || incomingUserId === '') {
        return false;
    }
    return String(currentUserId) === String(incomingUserId);
}

export function createSingleFlight(run) {
    if (typeof run !== 'function') {
        throw new TypeError('single-flight task must be a function');
    }

    let activePromise = null;
    return (...args) => {
        if (activePromise) {
            return activePromise;
        }

        activePromise = Promise.resolve()
            .then(() => run(...args))
            .finally(() => {
                activePromise = null;
            });
        return activePromise;
    };
}
